/**
 * Cache Manager - Intelligent Cache Updates
 *
 * Processes affected_entities from backend and performs surgical updates
 * to Redux store instead of clearing entire caches.
 *
 * ⚠️ CRITICAL: Uses injected axiosPrivate (from useAxiosPrivate hook)
 * not direct axios import, to maintain authorization headers and interceptors.
 *
 * Usage:
 *   const axiosPrivate = useAxiosPrivate();
 *   const manager = new CacheManager(dispatch, axiosPrivate);
 *   await manager.processAffectedEntities(response.data.affected_entities);
 */

import { update as updateLot, removeL as removeLot } from '../context/lots/lotsSlice';
import { update as updateTank, removeT as removeTank } from '../context/tanks/tanksSlice';
import { updateOp, removeOp } from '../context/operations/operationsSlice';
import { updateDrugItem } from '../context/substances/drugsSlice';
import { updateWarehouse, setWarehouse } from '../context/warehouse/warehouseSlice';
import { addStoredAnalysisResults, remove as removeAnalysisResult } from '../context/substances/storedAnalysisResultsSlice';

class CacheManager {
  /**
   * Initialize CacheManager
   * @param {Function} dispatch - Redux dispatch function
   * @param {Object} axiosPrivate - Axios instance from useAxiosPrivate hook
   */
  constructor(dispatch, axiosPrivate) {
    this.dispatch = dispatch;
    this.axios = axiosPrivate; // Injected axios instance with interceptors
    this.pendingRequests = new Map(); // Deduplication
  }

  /**
   * Process affected entities from backend
   * @param {Object} affectedEntities - Affected entities object from backend response
   */
  async processAffectedEntities(affectedEntities) {
    if (!affectedEntities) {
      console.warn('No affected entities to process');
      return;
    }

    const { batches, tanks, operations, drug_batches, warehouse, analysis_results } = affectedEntities;

    try {
      // Process in parallel for better performance
      await Promise.all([
        batches?.length > 0 ? this.processBatches(batches) : Promise.resolve(),
        tanks?.length > 0 ? this.processTanks(tanks) : Promise.resolve(),
        operations?.length > 0 ? this.processOperations(operations) : Promise.resolve(),
        drug_batches?.length > 0 ? this.processDrugBatches(drug_batches) : Promise.resolve(),
        warehouse?.length > 0 ? this.processWarehouse(warehouse) : Promise.resolve(),
        analysis_results?.length > 0 ? this.processAnalysisResults(analysis_results) : Promise.resolve(),
      ]);

      console.log('✅ Cache updated successfully');
    } catch (error) {
      console.error('❌ Failed to update cache:', error);
      // Consider fallback to emptyLots()/emptyTanks() here if needed
    }
  }

  /**
   * Process batch updates
   * @param {Array} batches - Array of { id, action }
   */
  async processBatches(batches) {
    console.log('📦 Processing batches:', batches);
    for (const { id, action } of batches) {
      if (action === 'delete') {
        this.dispatch(removeLot({ id }));
      } else if (action === 'update') {
        try {
          console.log(`🔄 Fetching batch ${id}...`);
          const response = await this.fetchBatch(id);
          console.log(`📡 Batch ${id} response:`, response);

          if (response?.data) {
            const batchesData = Array.isArray(response.data) ? response.data : [response.data];
            batchesData.forEach(batch => {
              console.log(`📤 Dispatching updateLot with data:`, batch);
              this.dispatch(updateLot(batch));
            });
            console.log(`✅ Batch ${id} dispatched to Redux`);
          } else {
            // console.warn(`⚠️ Batch ${id}: response.data is empty!`);
          }
        } catch (error) {
          console.error(`❌ Failed to fetch batch ${id}:`, error.message, error);
        }
      }
    }
  }

  /**
   * Process tank updates
   * @param {Array} tanks - Array of { id, action }
   */
  async processTanks(tanks) {
    console.log('🏺 Processing tanks:', tanks);
    for (const { id, action } of tanks) {
      if (action === 'delete') {
        this.dispatch(removeTank({ id }));
      } else if (action === 'update') {
        try {
          console.log(`🔄 Fetching tank ${id}...`);
          const response = await this.fetchTank(id);
          console.log(`📡 Tank ${id} response:`, response);

          if (response?.data) {
            const tanksData = Array.isArray(response.data) ? response.data : [response.data];
            tanksData.forEach(tank => {
              console.log(`📤 Dispatching updateTank with data:`, tank);
              this.dispatch(updateTank(tank));
            });
            console.log(`✅ Tank ${id} dispatched to Redux`);
          } else {
            console.warn(`⚠️ Tank ${id}: response.data is empty!`);
          }
        } catch (error) {
          console.error(`❌ Failed to fetch tank ${id}:`, error.message, error);
        }
      }
    }
  }

  /**
   * Process operation updates
   * @param {Array} operations - Array of { id, action }
   */
  async processOperations(operations) {
    for (const { id, action } of operations) {
      if (action === 'delete') {
        this.dispatch(removeOp({ id: Number(id) }));
      } else if (action === 'invalidate' || action === 'update') {
        try {
          const response = await this.fetchOperation(id);
          if (response?.data) {
            const opsData = Array.isArray(response.data) ? response.data : [response.data];
            opsData.forEach(op => {
              this.dispatch(updateOp(op));
            });
          }
        } catch (error) {
          console.error(`Failed to fetch operation ${id}:`, error);
        }
      }
    }
  }

  /**
   * Process drug batch updates
   * @param {Array} drug_batches - Array of { id, action }
   *
   * ⚠️ TODO: Verify drug batches endpoint with backend
   */
  async processDrugBatches(drug_batches) {
    const ids = drug_batches.map(db => db.id);

    try {
      // ⚠️ Adjust endpoint based on actual backend API
      const response = await this.axios.get(`/drugs/batches`, {
        params: { ids: ids.join(',') }
      });

      if (response?.data) {
        // Dispatch updates for each drug batch
        response.data.forEach(drugBatch => {
          this.dispatch(updateDrugItem(drugBatch));
        });
      }
    } catch (error) {
      console.error('Failed to fetch drug batches:', error);
    }
  }

  /**
   * Process warehouse updates
   * @param {Array} warehouse - Array of { type, action } or { type, id, action }
   *
   * ⚠️ Warehouse slice has complex structure with type-based organization
   * - Supports both old format: {type, action}
   * - And new format: {type, id, action} for granular updates
   * - Uses GET /warehouse/:type/:id for granular updates when IDs are provided
   */
  async processWarehouse(warehouse) {
    // Group warehouse updates by type for efficient processing
    const groupedByType = {};

    for (const item of warehouse) {
      const { type, action, id } = item;

      if (!groupedByType[type]) {
        groupedByType[type] = { action, ids: [] };
      }

      if (id) {
        groupedByType[type].ids.push(id);
      }
    }

    // Process each warehouse type
    for (const [type, { action, ids }] of Object.entries(groupedByType)) {
      if (action === 'update') {
        try {
          // If specific IDs are provided, fetch them individually (granular update)
          if (ids.length > 0) {
            console.log(`📦 Fetching ${ids.length} warehouse items: ${type} - IDs: ${ids.join(',')}`);

            // Fetch each specific item
            const items = [];
            for (const id of ids) {
              try {
                const response = await this.fetchWarehouseItem(type, id);
                if (response?.data) {
                  items.push(response.data);
                }
              } catch (error) {
                console.error(`Failed to fetch warehouse item ${type}/${id}:`, error);
              }
            }

            // If we got some items, update them (merge with existing)
            // Otherwise fallback to full fetch
            if (items.length > 0) {
              console.log(`✅ Updating ${items.length} warehouse items`);
              // Dispatch update for each item - warehouse slice will merge with existing
              items.forEach(item => {
                this.dispatch(updateWarehouse({
                  type,
                  id: item.id,
                  data: item
                }));
              });
            } else {
              console.warn(`⚠️ No items fetched, falling back to full warehouse fetch`);
              await this.fetchFullWarehouse(type);
            }
          } else {
            // No specific IDs, fetch entire warehouse type
            console.log(`📦 Fetching entire warehouse type: ${type}`);
            await this.fetchFullWarehouse(type);
          }
        } catch (error) {
          console.error(`Failed to process warehouse ${type}:`, error);
        }
      } else if (action === 'delete' && ids.length > 0) {
        // Handle deletion of specific warehouse items
        console.log(`🗑️ Deleting warehouse items: ${type} - IDs: ${ids.join(',')}`);
        // Refetch the entire warehouse type to reflect deletions
        try {
          await this.fetchFullWarehouse(type);
        } catch (error) {
          console.error(`Failed to fetch warehouse ${type} after delete:`, error);
        }
      }
    }
  }

  /**
   * Fetch full warehouse type data
   */
  async fetchFullWarehouse(type) {
    try {
      const response = await this.axios.get(`/warehouse/${type}`);
      if (response?.data) {
        this.dispatch(setWarehouse({
          type,
          data: response.data
        }));
      }
    } catch (error) {
      console.error(`Failed to fetch warehouse ${type}:`, error);
      throw error;
    }
  }

  /**
   * Fetch specific warehouse item with request deduplication
   */
  async fetchWarehouseItem(type, id) {
    const key = `warehouse-${type}-${id}`;

    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key);
    }

    const promise = this.axios.get(`/warehouse/${type}/${id}`)
      .finally(() => this.pendingRequests.delete(key));

    this.pendingRequests.set(key, promise);
    return promise;
  }

  /**
   * Process analysis results updates
   * @param {Array} analysis_results - Array of { id, action }
   *
   * ⚠️ TODO: Verify analysis results endpoint with backend
   */
  async processAnalysisResults(analysis_results) {
    const idsToFetch = [];
    const idsToRemove = [];

    // Separate add/update from delete actions
    for (const { id, action } of analysis_results) {
      if (action === 'delete') {
        idsToRemove.push(id);
      } else if (action === 'add' || action === 'update') {
        idsToFetch.push(id);
      }
    }

    // Handle deletions
    idsToRemove.forEach(id => {
      this.dispatch(removeAnalysisResult({ id }));
    });

    // Handle additions/updates - fetch all in one request
    if (idsToFetch.length > 0) {
      try {
        // ⚠️ Adjust endpoint based on actual backend API
        const response = await this.axios.get(`/analysis/results`, {
          params: { ids: idsToFetch.join(',') }
        });

        if (response?.data && Array.isArray(response.data)) {
          // Add/update all analysis results
          this.dispatch(addStoredAnalysisResults(response.data));
        }
      } catch (error) {
        console.error('Failed to fetch analysis results:', error);
      }
    }
  }

  // ========================================================================
  // Fetch methods with deduplication
  // ⚠️ CRITICAL: All use this.axios (injected axiosPrivate)
  // ========================================================================

  /**
   * Fetch batch with request deduplication
   */
  async fetchBatch(id) {
    const key = `batch-${id}`;

    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key);
    }

    const promise = this.axios.get(`/batches/${id}`)
      .finally(() => this.pendingRequests.delete(key));

    this.pendingRequests.set(key, promise);
    return promise;
  }

  /**
   * Fetch tank with request deduplication
   */
  async fetchTank(id) {
    const key = `tank-${id}`;

    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key);
    }

    const promise = this.axios.get(`/tanks/${id}`)
      .finally(() => this.pendingRequests.delete(key));

    this.pendingRequests.set(key, promise);
    return promise;
  }

  /**
   * Fetch operation with request deduplication
   */
  async fetchOperation(id) {
    const key = `operation-${id}`;

    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key);
    }

    const promise = this.axios.get(`/operations/${id}`)
      .finally(() => this.pendingRequests.delete(key));

    this.pendingRequests.set(key, promise);
    return promise;
  }
}

export default CacheManager;
